/**
 * LiveKit Service
 * Handles all LiveKit room and token management for Mentor Spaces.
 *
 * Backend expected endpoint: POST /livekit/token
 * Body: { roomName, participantName, participantId }
 * Returns: { token, wsUrl }
 */

import { postData, getData, deleteData } from './api';

// Fallback LiveKit WS URL — using the self-hosted server
export const LIVEKIT_WS_URL = 'wss://livekit.softextensions.com';

/**
 * Request a LiveKit token from the backend.
 * The backend will generate a JWT using the LiveKit SDK with proper grants.
 */
export const getLiveKitToken = async ({ roomName, participantName, participantId, role }) => {
    try {
        const response = await postData('/livekit/token', {
            roomName,
            participantName,
            participantId,
            role: role || 'participant'
        });
        return response; // { token, wsUrl }
    } catch (error) {
        console.error('[LiveKit] Error getting token:', error);
        throw error;
    }
};

/**
 * List active LiveKit rooms (for mentors to see who is live).
 */
export const getActiveRooms = async () => {
    try {
        const response = await getData('/livekit/rooms');
        return response || [];
    } catch (error) {
        console.error('[LiveKit] Error fetching rooms:', error);
        return [];
    }
};

/**
 * Get sessions by status (live, upcoming, history).
 */
export const getSessionsByStatus = async (status) => {
    try {
        const response = await getData(`/livekit/sessions/status/${status}`);
        return response || [];
    } catch (error) {
        console.error(`[LiveKit] Error fetching ${status} sessions:`, error);
        return [];
    }
};

/**
 * Create / make a room active via backend (mentor starts a space).
 */
export const createLiveKitRoom = async ({ roomName, mentorId, topic, description, scheduledStartTime, duration, allowedUserIds }) => {
    try {
        const response = await postData('/livekit/rooms/create', {
            roomName,
            mentorId,
            topic,
            description,
            scheduledStartTime,
            duration,
            allowedUserIds
        });
        return response;
    } catch (error) {
        console.error('[LiveKit] Error creating room:', error);
        throw error;
    }
};

/**
 * Schedule a mentor space for the future.
 */
export const scheduleMentorSession = async (scheduleData) => {
    try {
        const response = await postData('/livekit/sessions/schedule', scheduleData);
        return response;
    } catch (error) {
        console.error('[LiveKit] Error scheduling session:', error);
        throw error;
    }
};

/**
 * Build a sanitised room name from mentor info.
 */
export const buildRoomName = (mentorName, topic = 'session') => {
    const base = `${mentorName}-${topic}`
        .toLowerCase()
        .replace(/[^a-z0-9-]/g, '-')
        .replace(/-+/g, '-')
        .replace(/^-+|-+$/g, '') // Trim dashes from start and end
        .slice(0, 40); // Cap at 40 chars to leave room for timestamp suffix
    
    // Always append a timestamp to ensure uniqueness and preserve old sessions
    return `${base}-${Date.now().toString().slice(-5)}`;
};

/**
 * End an active session by sessionId — marks it completed & deletes the LiveKit room.
 * Only the host mentor can call this.
 */
export const endSession = async (sessionId) => {
    try {
        const response = await deleteData(`/livekit/sessions/${sessionId}`);
        return response;
    } catch (error) {
        console.error('[LiveKit] Error ending session:', error);
        throw error;
    }
};

/**
 * Update the YouTube video state for a session.
 */
export const updateYouTubeState = async (sessionId, state) => {
    try {
        const response = await postData(`/livekit/sessions/${sessionId}/youtube`, state);
        return response;
    } catch (error) {
        console.error('[LiveKit] Error updating YouTube state:', error);
        throw error;
    }
};

/**
 * End a session and delete its LiveKit room by roomName.
 */
export const deleteSessionByRoom = async (roomName) => {
    try {
        const response = await postData('/livekit/rooms/delete', { roomName });
        return response;
    } catch (error) {
        console.error('[LiveKit] Error deleting room:', error);
        throw error;
    }
};

/**
 * Fetch all currently active/ongoing sessions.
 */
export const fetchActiveSessions = () => getData('/livekit/sessions/status/ongoing');

/**
 * Fetch details for a specific session by ID.
 */
export const fetchSessionDetails = (id) => getData(`/livekit/sessions/${id}`);
